import React, { FC, memo, useEffect, useState } from 'react';
import { HandledComponentError } from '@components/ErrorHandling';
import { useTranslations } from '@hooks/useTranslations';
import { useJourneyType } from '@hooks/useJourneyType';
import { useDispatch, useSelector } from 'react-redux';
import {
    BRAND,
    C2S_WIDGET_QUERY_PARAMETER,
    isBrandDS,
    isBrandOV,
    isMarketGB, isProd,
    ModalVersion,
} from '../../../constants/main';
import Main from '@components/Layout/Main';
import { StyledGrid, ConfigRightSection, ConfigLeftSection } from '@components/Grid';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import Characteristics from '@components/v2/Characteristics';
import { SC } from '../../../styles/theme';
import ImageSection from '@components/v2/ImageSection';
import FAQs, { summaryTemplateId } from '@components/FAQs';
import SummaryTitle from '@components/SummaryTitle';
import BasketPriceDetails from '@components/BasketPriceDetails';
import BasketSummaryDetails from '@components/BasketSummaryDetails';
import Icon, { Icons } from '@components/Icon';
import { usePrices } from '@hooks/usePriceCalculator';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import PriceSuffix from '@components/PriceSuffix';
import { getMonthlyPriceObjByType, hasFinanceVariants } from '@utils/Price.utils';
import InterestedBox from '@components/v2/InterestedBox';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useGTM } from '@hooks/useGTM';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { useCheckStockCarAvailabilityCheck } from '@hooks/useStockCarAvailabilityCheck';
import { PaymentJourneyTypes } from '../../../partExchange/interfaces/Default';
import FilterDuck from '../../../redux/filters/filter.duck';
import { CASH } from '../../../services/filters/filters';
import StepNavigation from '@components/v2/StepNavigation';
import { Pages } from '../../../types/pages';
import { GtmData } from '../../../types/gtm';
import { useCreateDealPerJourney } from '@hooks/useCreateDealPerJourney';
import { useUpdateDealForBasketToCheckout } from '@hooks/useUpdateDealForBasketToCheckout';
import { isEmpty } from 'lodash';
import RedirectionModal from '@components/RedirectionModal';
import useCustomRouter from '@hooks/useCustomRouter';
import { BUDGET_TYPE_CASH, BUDGET_TYPE_FINANCE, DealService } from '../../../services';
import { ConfigMainGrid } from './BasketStyled';
import { useGetCustomerType } from '@hooks/useGetCustomerType';
import { useEffectOnce, useMedia } from 'react-use';
import { breakpoints } from 'src/styles/theme';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { useExperiment } from '@hooks/useExperiment';
import GlobalDuck from 'src/redux/global/global.duck';
import { Redux } from 'src/redux/redux.interface';
import { GTM_EVENT_AB_TEST } from 'src/constants/gtm';
import { useUserDuck } from '@hooks/useUserDuck';
import { getSessionIdCookie } from '@utils/Session.utils';
import { GTM_MAIN_STEP } from 'src/constants/gtm';
import useLogoutListener from '@hooks/useLogoutListener';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import useSessionChangeListener from '@hooks/useSessionChangeListener';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import { syncPartExchange } from '../../../partExchange/service/ApiService';
import ConfiguratorDuck from '../../../redux/configurator/configurator.duck';
import { processMopData } from '../../../partExchange/utility/general';
import { PX_STATUSES_IN_PROGRESS } from '../../../partExchange/constants/config';
import { PosthogTrigger } from '../../../constants/enums';

const BasketTemplate: FC<SC> = memo(({ className }) => {
    const { isUserLoggedInAsDealer } = useUserDuck();
    const [isMounted, setIsMounted] = useState(false);
    const customerType = useGetCustomerType();
    const [, setIsShowOffersDialog] = useState(false);
    const [showPriceSection, setShowPriceSection] = useState(true);
    const dispatch = useDispatch();
    const router = useCustomRouter();
    const { t } = useTranslations();
    const { pushProductDetailImpression, pushBasketPageLoad, pushToDataLayer } = useGTM();
    const { formatMoney } = useMoneyFormatter();
    const { carDetails, currentDeal, dealDealerLayerEnabled } = useCarDetailsDuck();
    const { paymentJourneyType, isFinance } = useJourneyType();
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);
    const { pushTrigger, getPageNameByUrl } = useExperiment();
    const [isOpenPriceSummary, setIsOpenPriceSummary] = useState(false);
    const [isOpenPriceDetails, setIsOpenPriceDetails] = useState(false);
    const [wasFirmTradePopinOpen, setWasFirmTradePopinOpen] = useState(false);
    const { isTracked } = useGlobalDuck();
    const { plausibleUserEvent } = usePlausible();
    const { finance, cash } = usePrices();

    const monthlyPriceObj = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const amountRounding = monthlyPriceObj?.amountRounding;

    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const isDealerLayerEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_LAYER_ENABLED);
    const isBasketStepperEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_BASKET_STEPS_ENABLED);
    const isPosthogEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_POSTHOG_ENABLED);

    const { isStockJourney } = useCarStockJourneyCheck();

    const { handleTabChange } = useSessionChangeListener();

    useEffect(() => {
        document?.addEventListener('visibilitychange', handleTabChange);
    }, []);

    useEffect(() => {
        const c2sId = router.query[C2S_WIDGET_QUERY_PARAMETER];

        if (
            c2sId &&
            !wasFirmTradePopinOpen &&
            autobizPxState?.statusId &&
            PX_STATUSES_IN_PROGRESS.includes(autobizPxState?.statusId)
        ) {
            setTimeout(() => {
                // @ts-ignore
                if (typeof window?.Clic2sell !== 'undefined') {
                    // @ts-ignore
                    const c2s = new window.Clic2sell({
                        env: isProd ? 'prod' : 'ppr',
                        language: process.env.NEXT_PUBLIC_LANGUAGE,
                        folderId: c2sId,
                    });

                    c2s.open();

                    const callBack = async () => {
                        const removeListener = () => {
                            document.removeEventListener('click', callBack);
                        };

                        const element = document.getElementById('c2smodal');
                        if (!element) {
                            console.log('C2S WIDGET WAS CLOSED');
                            const { data } = await syncPartExchange(currentDeal?.token);
                            dispatch(ConfiguratorDuck.setPxState(processMopData(data?.data)));
                            setWasFirmTradePopinOpen(true);
                            removeListener();
                        }
                    };

                    document.addEventListener('click', callBack);
                }
            }, 1000);
        }
    }, [autobizPxState, wasFirmTradePopinOpen]);

    useEffect(() => {
        new Promise((resolve) => setTimeout(resolve, 7500)).then(() => {
            const element = document.getElementById('c2smodal');

            console.log('HAS ELEMENT', element, autobizPxState);
            if (element) {
                const callBack = async () => {
                    const removeListener = () => {
                        document.removeEventListener('click', callBack);
                    };

                    const element = document.getElementById('c2smodal');
                    if (!element) {
                        console.log('C2S WIDGET WAS CLOSED');
                        const { data } = await syncPartExchange(currentDeal?.token);
                        dispatch(ConfiguratorDuck.setPxState(processMopData(data?.data)));
                        removeListener();
                    }
                };

                document.addEventListener('click', callBack);
            }
        });
    }, [autobizPxState]);

    const togglePriceSummary = () => {
        setIsOpenPriceSummary(!isOpenPriceSummary);
    };

    const shouldDisplayGetPersonalisedOffer = dealDealerLayerEnabled && isDealerLayerEnabled;
    const hasDealer = currentDeal?.fullProductConfiguration?.dealers?.length > 0 || currentDeal?.dealerId;

    if (isStockJourney) {
        const hasMonthlyPrice = hasFinanceVariants(carDetails.prices);
        const isSwitcherEnabled = Boolean(hasMonthlyPrice) && isBudgetChangeAllowed;
        if (!isSwitcherEnabled && paymentJourneyType === PaymentJourneyTypes.FINANCE) {
            dispatch(FilterDuck.changeBudgetType(CASH));
        }
    }

    useEffect(() => {
        if (isPosthogEnabled && !isTracked) {
            pushToDataLayer({
                event: 'uaevent',
                eventCategory: GTM_EVENT_AB_TEST,
                eventAction: 'summary-page-test', //Campaign name
                eventLabel: 'hide', //Variation name
            } as GtmData);
            dispatch(GlobalDuck.setIsTracked(true));
        }
    }, [isTracked]);

    useEffect(() => {
        const paymentJourney = router?.query?.paymentJourney;
        if (paymentJourney && [BUDGET_TYPE_CASH, BUDGET_TYPE_FINANCE].includes(String(paymentJourney))) {
            dispatch(FilterDuck.changeBudgetType(paymentJourney as string));
        }

        dispatch(UIDuck.setShowBasketInHeader(true));
    }, []);

    useCheckStockCarAvailabilityCheck(
        currentDeal?.fullProductConfiguration.model.title,
        currentDeal?.fullProductConfiguration.nameplateBodyStyleSlug,
        paymentJourneyType,
        currentDeal?.fullProductConfiguration?.stockSpecificFields?.booked
    );

    useEffect(() => {
        if (Object.keys(carDetails).length === 0 && carDetails.constructor === Object && !isMounted) {
            return;
        }
    }, [paymentJourneyType, isMounted, customerType]);

    useEffect(() => {
        pushBasketPageLoad(paymentJourneyType, carDetails, isStockJourney, currentDeal);
    }, [paymentJourneyType, customerType]);

    useEffect(() => {
        setIsMounted(true);
        if (shouldDisplayGetPersonalisedOffer) {
            const queryParams = new URLSearchParams(window.location.search);
            const isWithOffersModalUrlParam = !!queryParams.get('withOffersModal');
            setIsShowOffersDialog(isWithOffersModalUrlParam);
        }

        if (Object.keys(carDetails).length === 0 && carDetails.constructor === Object) {
            return;
        }
        pushProductDetailImpression('basket', carDetails, isStockJourney);
        window.scrollTo(0, 0);
    }, []); // Same As ComponentDidMount, add NO conditions !!!

    useUpdateDealForBasketToCheckout();
    useCreateDealPerJourney(isMounted);

    useEffect(() => {
        window.addEventListener('scroll', () => {
            if (window.pageYOffset + 200 > document.getElementById(summaryTemplateId)?.offsetTop) {
                return setShowPriceSection(false);
            }

            setShowPriceSection(true);
        });
    }, []);

    useEffect(() => {
        pushToDataLayer({
            eventCategory: 'SolRetail::Basket',
            eventAction: 'BasketView::NonInteractive',
            eventLabel: 'Basket view',
        } as GtmData);
    }, []);

    const isLargeBreakPointOrWider = useMedia(`(min-width: ${breakpoints.lg}px)`, true);
    useEffect(() => {
        if (isLargeBreakPointOrWider) {
            const carGallery = document.getElementById('carGallery');
            if (hasDealer) {
                const vehicleImageSection = document.getElementById('vehicleImageSection');

                if (carGallery && vehicleImageSection) {
                    carGallery.style.height = `${vehicleImageSection?.offsetHeight + 20}px`;
                }
            } else {
                if (carGallery) {
                    carGallery.style.height = 'auto';
                }
            }
        }
    }, [hasDealer]);

    useLogoutListener();

    const showRedirectionModal = isEmpty(carDetails) || !currentDeal;

    try {
        return (
            <div className={className}>
                <div>
                    <div className={`basket ${isOpenPriceSummary ? 'isOpenPriceSummary' : ''}`}>
                        {isOpenPriceSummary && (
                            <div className="basketSummaryDetailsPopover">
                                <BasketSummaryDetails closePriceSummary={togglePriceSummary} />
                            </div>
                        )}
                        <Main title="" description="" canonicalLink="" whiteBackground>
                            {showRedirectionModal ? (
                                <RedirectionModal />
                            ) : (
                                <>
                                    <StyledGrid brand={BRAND} noPadding>
                                        {isBasketStepperEnabled && (
                                            <div className="sticky">
                                                <StepNavigation isUserLoggedInAsDealer={isUserLoggedInAsDealer} />
                                            </div>
                                        )}
                                        <ConfigMainGrid>
                                            <ConfigLeftSection>
                                                <SummaryTitle isBasket />
                                                <ImageSection isBasket />
                                                <BasketPriceDetails
                                                    isOpenPriceDetails={isOpenPriceDetails}
                                                    setIsOpenPriceDetails={setIsOpenPriceDetails}
                                                />
                                                <InterestedBox
                                                    onClickCta={() => plausibleUserEvent(UserEvent.ContinueCtaBasket)}
                                                    page={Pages.basketV2}
                                                />
                                            </ConfigLeftSection>
                                            <ConfigRightSection isBasket withBorder={isBrandDS}>
                                                <BasketSummaryDetails
                                                    isTopMargin={isUserLoggedInAsDealer && isBasketStepperEnabled}
                                                    isUserLoggedInAsDealer={isUserLoggedInAsDealer}
                                                    setIsOpenPriceDetails={setIsOpenPriceDetails}
                                                />
                                            </ConfigRightSection>
                                            {showPriceSection && (
                                                <div className="mobilePriceSection">
                                                    <div
                                                        className="mobilePriceSectionArrow"
                                                        onClick={togglePriceSummary}
                                                    >
                                                        <Icon width={24} height={24} name={Icons.ChevronUp} />
                                                    </div>
                                                    <div className="mobilePriceSectionPrice">
                                                        <span>
                                                            {isFinance
                                                                ? t('basket.summary.monthlyPrice')
                                                                : t('offerDetail.finalPrice')}
                                                        </span>
                                                        <span>
                                                            <span className="price">
                                                                {formatMoney(
                                                                    isFinance ? finance.totalPrice : cash.totalPrice,
                                                                    true,
                                                                    '',
                                                                    amountRounding
                                                                )}
                                                            </span>
                                                            <PriceSuffix isMonthlyPrice={isFinance} />
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="mobilePriceSectionHelp"
                                                        onClick={() => {
                                                            dispatch(
                                                                UIDuck.openModal({
                                                                    data: {},
                                                                    modalType: UIDuck.MODAL_TYPES.NEED_HELP,
                                                                    modalVersion: ModalVersion.v2,
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        <span className="label">
                                                            {t('header.nav.needHelp')}{' '}
                                                            <Icon name={Icons.ChevronRight} width={11} height={11} />
                                                        </span>
                                                        <span className="phone">
                                                            <Icon
                                                                name={
                                                                    isBrandOV && !isMarketGB
                                                                        ? Icons.PhoneV2
                                                                        : Icons.Phone
                                                                }
                                                                width={24}
                                                                height={24}
                                                            />{' '}
                                                            {t('support.telephone')}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </ConfigMainGrid>
                                    </StyledGrid>
                                    <div id="characteristics">
                                        <Characteristics />
                                    </div>
                                    <FAQs />
                                </>
                            )}
                        </Main>
                    </div>
                </div>
            </div>
        );
    } catch (e: any) {
        return <HandledComponentError error={e} />;
    }
});
BasketTemplate.displayName = 'BasketTemplate';
export default BasketTemplate;
